import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChannelBadge } from "@/components/ChannelBadge";
import { productImage } from "@/lib/images";
import { fmtTL, ExpiryBadge, StockBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, Store, Truck, Globe, ShoppingBag, X, History,
  ExternalLink, Sparkles, Search, Pencil, Trash2, Printer, Bot, Copy, Lock, Tag,
} from "lucide-react";

const PRICE_LABELS = [
  { key: "market_retail", label: "Market Perakende", icon: Store },
  { key: "market_web", label: "Kendi Site", icon: Globe },
  { key: "marketplace", label: "Pazaryeri", icon: ShoppingBag },
  { key: "wholesale_pickup", label: "Toptan Depo", icon: Truck },
  { key: "wholesale_delivered", label: "Toptan Rota", icon: Truck },
];

const CH_META = {
  PER: { label: "Perakende Mağaza", icon: Store },
  ETC: { label: "E-Ticaret (Site)", icon: Globe },
  PAZAR: { label: "Pazaryeri", icon: ShoppingBag },
  TOP: { label: "Toptan (Depo)", icon: Truck },
  ROTA: { label: "Toptan (Rota)", icon: Truck },
};
const LEGACY_TO_CODE = { market_retail: "PER", market_web: "ETC", marketplace: "PAZAR", wholesale_pickup: "TOP", wholesale_delivered: "ROTA" };

export default function ProductDialog({ product, onClose, onUpdate, canPrice, onEdit, onDeleted }) {
  const nav = useNavigate();
  const { can, user } = useAuth();
  const showPurchase = can("purchase.read");
  const showMarket = can("price.read");
  const canEdit = can("catalog.write");
  const canDelete = can("catalog.delete");
  const [p, setP] = useState(product);
  const [scraping, setScraping] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [labelCopies, setLabelCopies] = useState(1);
  const [printingLabel, setPrintingLabel] = useState(false);
  const [descChannel, setDescChannel] = useState("marketplace");
  const [aiDesc, setAiDesc] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [genImg, setGenImg] = useState(false);
  const [qsOpen, setQsOpen] = useState(false);
  const [qsLocs, setQsLocs] = useState([]);
  const [qsLoc, setQsLoc] = useState("");
  const [qsQty, setQsQty] = useState("");
  const [qsBusy, setQsBusy] = useState(false);
  const isAdminUser = user?.role === "admin";
  const ma = p.market_avg || {};
  const research = p.market_research || {};
  const sources = research.sources || [];
  const okSources = sources.filter((s) => s.status === "OK" && ((s.best && s.best.price != null) || s.low != null));
  const otherSources = sources.filter((s) => s.status !== "OK");
  const opp = p.opportunity;

  // Kanonik model: ürün yalnız ait olduğu kanallarda fiyatlanır (kısmi fiyatlama).
  const hasMarketCat = (p.market_categories?.length || 0) > 0;
  const hasWholeCat = (p.wholesale_categories?.length || 0) > 0;
  const hasMarket = hasMarketCat || (!hasWholeCat && p.channel_type !== "toptan");
  const hasWholesale = hasWholeCat || (!hasMarketCat && p.channel_type === "toptan");
  const WHOLESALE_KEYS = ["wholesale_pickup", "wholesale_delivered"];
  const relevantLabels = PRICE_LABELS.filter(({ key }) => (WHOLESALE_KEYS.includes(key) ? hasWholesale : hasMarket));
  const isWholesaleCode = (code) => code === "TOP" || code === "ROTA";

  const [editPrices, setEditPrices] = useState(false);
  const [priceInputs, setPriceInputs] = useState({});
  const [savingPrices, setSavingPrices] = useState(false);
  const startEditPrices = () => {
    const sp = p.sales_prices || {};
    setPriceInputs(relevantLabels.reduce((m, { key }) => ({ ...m, [key]: sp[key] ? String(sp[key]) : "" }), {}));
    setEditPrices(true);
  };
  const savePrices = async () => {
    const payload = {};
    for (const { key } of relevantLabels) {
      const v = Number(priceInputs[key]);
      if (v && v > 0) payload[key] = v;
    }
    if (Object.keys(payload).length === 0) { toast.error("En az bir kanal fiyatı girin (fiyat > 0)"); return; }
    setSavingPrices(true);
    try {
      const res = await api.post(`/products/${p.id}/prices`, payload);
      if (res.data?.pending_approval) {
        toast.info(res.data.message || "Fiyat değişikliği onay için ilgili kişiye gönderildi");
      } else {
        const updated = res.data?.channel_prices ? res.data : { ...p, sales_prices: { ...(p.sales_prices || {}), ...payload } };
        setP(updated); onUpdate(updated);
        toast.success("Fiyatlar kaydedildi");
      }
      setEditPrices(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingPrices(false); }
  };

  const runResearch = async () => {
    setScraping(true);
    try {
      const { data } = await api.post(`/products/${p.id}/refresh-market-price`);
      const updated = { ...p, market_avg: data.market_avg, market_research: data.market_research, opportunity: data.opportunity, market_rejected: data.market_rejected ?? p.market_rejected };
      setP(updated);
      onUpdate(updated);
      const ok = data.market_research?.sources_ok || 0;
      if (data.opportunity?.found) toast.success(`Fırsat bulundu: ${data.opportunity.site} — %${data.opportunity.saving_pct} daha ucuz!`);
      else if (ok > 0) toast.success(`${ok} sitede fiyat bulundu`);
      else toast.warning("Sitelerden veri alınamadı (engellenmiş olabilir).");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setScraping(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`"${p.name}" ürün kartını silmek istediğinize emin misiniz?`)) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/products/${p.id}`);
      if (res.data?.pending_approval) toast.info(res.data.message || "Silme Yönetici onayına gönderildi");
      else { toast.success("Ürün kartı silindi"); onDeleted?.(p.id); }
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const printLabel = async () => {
    setPrintingLabel(true);
    try {
      const res = await api.post("/products/labels",
        { channel: p.channel_type === "toptan" ? "toptan" : "market", items: [{ product_id: p.id, copies: Number(labelCopies) || 1 }] },
        { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) { toast.error("Etiket oluşturulamadı"); }
    finally { setPrintingLabel(false); }
  };

  const genDesc = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/product-description", { product_id: p.id, channel: descChannel });
      setAiDesc(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Metin üretilemedi"); }
    finally { setAiBusy(false); }
  };
  const copyDesc = () => {
    if (!aiDesc) return;
    const t = `${aiDesc.title}\n\n${aiDesc.description}\n\n${(aiDesc.bullets || []).map((b) => "• " + b).join("\n")}\n\nAnahtar kelimeler: ${(aiDesc.keywords || []).join(", ")}`;
    navigator.clipboard.writeText(t);
    toast.success("Metin panoya kopyalandı");
  };

  const hist = (p.purchase_history || []).slice(-3).reverse();

  const genImage = async () => {
    setGenImg(true);
    try {
      const { data } = await api.post(`/products/${p.id}/generate-image`);
      const updated = { ...p, images: [data.url], ai_image: true };
      setP(updated); onUpdate(updated);
      toast.success("AI ürün görseli üretildi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "AI görsel üretilemedi");
    } finally { setGenImg(false); }
  };

  const openQuickStock = async () => {
    setQsOpen(true);
    if (qsLocs.length === 0) {
      try {
        const { data } = await api.get("/locations");
        const locs = (data || []).filter((l) => l.is_active !== false);
        setQsLocs(locs);
        if (locs[0]) setQsLoc(locs[0].id);
      } catch { /* ignore */ }
    }
  };
  const saveQuickStock = async () => {
    if (!qsLoc) return toast.error("Lokasyon seçin");
    if (qsQty === "" || Number.isNaN(Number(qsQty))) return toast.error("Miktar girin");
    setQsBusy(true);
    try {
      const { data } = await api.post(`/products/${p.id}/quick-stock`, { location_id: qsLoc, quantity: Number(qsQty) });
      const updated = { ...p, stock_on_hand: data.stock_on_hand };
      setP(updated); onUpdate(updated);
      toast.success("Stok güncellendi");
      setQsOpen(false); setQsQty("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Stok güncellenemedi"); }
    finally { setQsBusy(false); }
  };

  const tabCount = 1 + (showMarket ? 1 : 0) + (showPurchase ? 1 : 0) + (canEdit ? 1 : 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl overflow-hidden p-0" data-testid="product-dialog">
        <DialogTitle className="sr-only">{p.name}</DialogTitle>
        <DialogDescription className="sr-only">{p.category} · {p.sku} ürün detay kartı</DialogDescription>
        <div className="relative h-28 w-full overflow-hidden bg-slate-100">
          <img src={productImage(p)} alt={p.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <button onClick={onClose} data-testid="dialog-close" className="absolute right-3 top-3 rounded-full bg-white/90 p-1.5 hover:bg-white">
            <X className="h-4 w-4" />
          </button>
          {canEdit && !p.fresh_image_lock && (
            <button onClick={genImage} disabled={genImg} data-testid="pd-gen-image"
              title="Gemini Nano Banana ile ürün görseli üret"
              className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-white disabled:opacity-60">
              {genImg ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-brand-orange" />}
              {p.images?.length ? "AI ile Yenile" : "AI Görsel Üret"}
            </button>
          )}
          <div className="absolute bottom-2.5 left-4 right-4">
            <div className="mb-1 flex flex-wrap gap-1">
              {(p.market_categories || []).map((c) => (
                <span key={`m-${c}`} className="inline-flex items-center gap-1 rounded-sm bg-brand-orange px-1.5 py-0.5 text-[10px] font-bold text-white"><Store className="h-2.5 w-2.5" /> {c}</span>
              ))}
              {(p.wholesale_categories || []).map((c) => (
                <span key={`w-${c}`} className="inline-flex items-center gap-1 rounded-sm bg-sky-600 px-1.5 py-0.5 text-[10px] font-bold text-white"><Truck className="h-2.5 w-2.5" /> {c}</span>
              ))}
              {!(p.market_categories?.length || p.wholesale_categories?.length) && (
                <span className="inline-flex items-center rounded-sm bg-brand-orange px-1.5 py-0.5 text-[10px] font-bold text-white"><ChannelBadge channel={p.channel_type} upper /> · {p.category}</span>
              )}
            </div>
            <h2 className="font-heading text-lg font-bold text-white">{p.name}</h2>
          </div>
        </div>

        <div className="p-4">
          {/* Kompakt meta satırı — kaydırma yok */}
          <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="font-mono text-xs text-slate-500">{p.sku}</span>
            {p.barcode && <span className="font-mono text-xs text-slate-400">| {p.barcode}</span>}
            <StockBadge qty={p.stock_on_hand} unit={p.base_unit} />
            {isAdminUser && (
              <button data-testid="quick-stock-btn" onClick={openQuickStock} title="Hızlı stok düzenle (Yönetici)"
                className="inline-flex items-center gap-1 rounded-sm bg-brand-orange/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-orange hover:bg-brand-orange/20"><Pencil className="h-3 w-3" /> Stok</button>
            )}
            {p.expiry_date && <ExpiryBadge dateStr={p.expiry_date} />}
            <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">KDV %{p.vat_rate}</span>
            {p.fresh_image_lock && (
              <span data-testid="fresh-lock-badge" title="Firma görseli — internetten değiştirilmez" className="inline-flex items-center gap-1 rounded-sm bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700"><Lock className="h-3 w-3" /> Firma Görseli</span>
            )}
            {p.kdv_manual_check && (
              <span data-testid="kdv-manual-badge" className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">KDV doğrula</span>
            )}

            {/* Sağa yaslı hızlı aksiyonlar */}
            <div className="ml-auto flex items-center gap-1.5">
              {canEdit && (
                <Button size="sm" variant="outline" data-testid="product-edit-btn" onClick={() => onEdit?.(p)} className="h-8 px-2.5 text-xs">
                  <Pencil className="mr-1 h-3.5 w-3.5" /> Düzenle
                </Button>
              )}
              {canDelete && (
                <Button size="sm" variant="outline" data-testid="product-delete-btn" onClick={handleDelete} disabled={deleting}
                  className="h-8 border-red-200 px-2 text-xs text-red-600 hover:bg-red-50">
                  {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              )}
            </div>
          </div>

          {qsOpen && isAdminUser && (
            <div data-testid="quick-stock-panel" className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-brand-orange/30 bg-orange-50/60 p-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-500">Lokasyon</label>
                <select data-testid="quick-stock-loc" value={qsLoc} onChange={(e) => setQsLoc(e.target.value)} className="mt-0.5 h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange">
                  {qsLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-500">Yeni Miktar</label>
                <Input data-testid="quick-stock-qty" inputMode="decimal" value={qsQty} onChange={(e) => setQsQty(e.target.value)} placeholder="0" className="mt-0.5 h-9 w-28 tabular" />
              </div>
              <Button data-testid="quick-stock-save" onClick={saveQuickStock} disabled={qsBusy} size="sm" className="h-9 bg-brand-orange text-white hover:bg-orange-600">{qsBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
              <Button data-testid="quick-stock-cancel" variant="ghost" size="sm" onClick={() => setQsOpen(false)} className="h-9">İptal</Button>
            </div>
          )}

          <Tabs defaultValue="prices" className="w-full">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-lg bg-slate-100 p-1" data-testid="pd-tabs">
              <TabsTrigger value="prices" data-testid="pd-tab-prices" className="flex-1">Fiyatlar</TabsTrigger>
              {showMarket && <TabsTrigger value="market" data-testid="pd-tab-market" className="flex-1">Piyasa</TabsTrigger>}
              {showPurchase && <TabsTrigger value="purchase" data-testid="pd-tab-purchase" className="flex-1">Alış</TabsTrigger>}
              {canEdit && <TabsTrigger value="tools" data-testid="pd-tab-tools" className="flex-1">AI &amp; Etiket</TabsTrigger>}
            </TabsList>

            <div className="mt-3 max-h-[52vh] overflow-y-auto pr-1">
              {/* --- Fiyatlar --- */}
              <TabsContent value="prices" className="mt-0">
                {(p.alias_skus?.length > 0) && (
                  <div data-testid="alias-skus" className="mb-3 flex flex-wrap items-center gap-1.5 rounded-md bg-slate-50 px-3 py-2">
                    <Tag className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-[11px] font-semibold text-slate-500">Alias / Eski SKU:</span>
                    {p.alias_skus.slice(0, 8).map((a) => (
                      <span key={a} className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-500 ring-1 ring-inset ring-slate-200">{a}</span>
                    ))}
                    {p.alias_skus.length > 8 && <span className="text-[10px] text-slate-400">+{p.alias_skus.length - 8} daha</span>}
                  </div>
                )}
                <div className="mb-2 flex items-center justify-between">
                  <p className="overline">Bizim Satış Fiyatlarımız (Kanallar)</p>
                  {canPrice && !editPrices && (
                    <Button size="sm" variant="outline" data-testid="pd-edit-prices-btn" onClick={startEditPrices} className="h-7 text-xs">
                      <Pencil className="mr-1 h-3 w-3" /> Fiyat Düzenle
                    </Button>
                  )}
                </div>
                {editPrices ? (
                  <div className="space-y-1.5" data-testid="pd-price-editor">
                    <p className="rounded-sm bg-emerald-50 px-2 py-1 text-[10px] text-emerald-700 ring-1 ring-inset ring-emerald-200">Yalnız sattığınız kanalları girin. Boş bıraktığınız kanallar korunur.</p>
                    {relevantLabels.map(({ key, label, icon: Icon }) => (
                      <div key={key} className="flex items-center justify-between gap-2 rounded-sm bg-slate-50 px-3 py-1.5">
                        <span className="flex items-center gap-2 text-sm text-slate-600"><Icon className="h-3.5 w-3.5 text-slate-400" /> {label}</span>
                        <Input data-testid={`pd-price-${key}`} type="number" min="0" value={priceInputs[key] ?? ""}
                          onChange={(e) => setPriceInputs((m) => ({ ...m, [key]: e.target.value }))} className="h-8 w-28 tabular" placeholder="0" />
                      </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-1">
                      <Button size="sm" variant="outline" data-testid="pd-cancel-prices" onClick={() => setEditPrices(false)} className="h-8 text-xs">İptal</Button>
                      <Button size="sm" data-testid="pd-save-prices" onClick={savePrices} disabled={savingPrices} className="h-8 bg-brand-green text-xs text-white hover:bg-brand-green-600">
                        {savingPrices ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null} Kaydet
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5" data-testid="pd-channel-list">
                    {(() => {
                      const raw = (p.channel_prices && p.channel_prices.length)
                        ? p.channel_prices
                        : PRICE_LABELS.map(({ key }) => ({ code: LEGACY_TO_CODE[key], price: p.sales_prices?.[key] || 0, active: (p.sales_prices?.[key] || 0) > 0, min_order: 1 }));
                      const list = raw.filter((cp) => (isWholesaleCode(cp.code) ? hasWholesale : hasMarket));
                      return list.map((cp) => {
                        const meta = CH_META[cp.code] || { label: cp.code, icon: ShoppingBag };
                        const Icon = meta.icon;
                        const price = cp.price;
                        const margin = showPurchase && p.last_cost && price ? (((price / (1 + p.vat_rate / 100)) - p.last_cost) / (price / (1 + p.vat_rate / 100)) * 100) : null;
                        return (
                          <div key={cp.code} data-testid={`pd-ch-${cp.code}`} className={`flex items-center justify-between rounded-sm px-3 py-2 ${cp.active ? "bg-slate-50" : "bg-slate-50/50 opacity-60"}`}>
                            <span className="flex items-center gap-2 text-sm text-slate-600">
                              <Icon className="h-3.5 w-3.5 text-slate-400" /> {meta.label}
                              {!cp.active && <span className="rounded bg-slate-200 px-1 text-[9px] text-slate-500">PASİF</span>}
                              {cp.min_order > 1 && <span className="rounded bg-amber-100 px-1 text-[9px] text-amber-700">min {cp.min_order}</span>}
                              {p.channel_content?.[cp.code] && (p.channel_content[cp.code].image || p.channel_content[cp.code].description) && <span className="rounded bg-sky-100 px-1 text-[9px] text-sky-700">özel içerik</span>}
                            </span>
                            <div className="flex items-center gap-3">
                              {margin != null && cp.active && <span className={`tabular text-[11px] font-medium ${margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>marj %{margin.toFixed(1)}</span>}
                              <span className={`tabular text-sm font-bold ${price ? "text-slate-900" : "text-red-500"}`}>{price ? fmtTL(price) : "Fiyatsız"}</span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </TabsContent>

              {/* --- Piyasa --- */}
              {showMarket && (
                <TabsContent value="market" className="mt-0">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="overline">Piyasa Araştırması · {research.sources_total || 19} Site</p>
                    {canPrice && (
                      <Button size="sm" variant="outline" data-testid="refresh-market-btn" onClick={runResearch} disabled={scraping} className="h-7 text-xs">
                        {scraping ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Search className="mr-1 h-3 w-3" />} Piyasa Araştır
                      </Button>
                    )}
                  </div>
                  <p className="mb-2 flex items-center gap-1 text-[10px] text-slate-400" data-testid="pd-market-sources-hint">
                    <Globe className="h-3 w-3" /> Araştırılan siteler <b className="mx-0.5 font-semibold text-slate-500">Pazar Araştırması › Kaynaklar & Ayarlar</b>'dan düzenlenir.
                  </p>
                  <div className="mb-3 grid grid-cols-3 gap-2">
                    <div className="rounded-sm bg-slate-50 p-2 text-center">
                      <p className="text-[10px] text-slate-400">En Düşük</p>
                      <p className="tabular text-sm font-bold text-emerald-700">{ma.low != null ? fmtTL(ma.low) : "—"}</p>
                    </div>
                    <div className="rounded-sm bg-orange-50 p-2 text-center ring-1 ring-inset ring-orange-200">
                      <p className="text-[10px] text-orange-500">Ortalama</p>
                      <p className="tabular text-sm font-bold text-orange-700">{ma.avg != null ? fmtTL(ma.avg) : "—"}</p>
                    </div>
                    <div className="rounded-sm bg-slate-50 p-2 text-center">
                      <p className="text-[10px] text-slate-400">En Yüksek</p>
                      <p className="tabular text-sm font-bold text-slate-700">{ma.high != null ? fmtTL(ma.high) : "—"}</p>
                    </div>
                  </div>

                  {sources.length > 0 ? (
                    <div className="space-y-2.5">
                      <div className="max-h-56 space-y-1.5 overflow-y-auto overscroll-contain pr-1" data-testid="market-source-list">
                        {okSources.length === 0 ? (
                          <p className="text-xs text-slate-400">Taranan sitelerde geçerli fiyat bulunamadı.</p>
                        ) : okSources.map((s, i) => {
                          const price = (s.best && s.best.price != null) ? s.best.price : s.low;
                          const cheap = showPurchase && p.last_cost > 0 && price < p.last_cost;
                          return (
                            <div key={i} data-testid={`market-source-${i}`} className="flex items-center gap-2.5 rounded-md bg-white px-2.5 py-2 ring-1 ring-inset ring-slate-200">
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-[10px] font-bold text-slate-500">{s.type === "AI" ? "AI" : (s.site || "?").slice(0, 2).toUpperCase()}</span>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-xs font-semibold text-slate-700">{s.site}</p>
                                {s.best?.title ? <p className="truncate text-[10px] text-slate-400">{s.best.title}</p> : <p className="text-[10px] text-slate-400">{s.type === "AI" ? "Google AI" : "perakende/toptan"}{s.count > 1 ? ` · ${s.count} ilan` : ""}</p>}
                              </div>
                              <span className={`tabular shrink-0 text-sm font-bold ${cheap ? "text-emerald-600" : "text-slate-900"}`}>{fmtTL(price)}</span>
                              {s.best?.url ? (
                                <a data-testid={`market-source-link-${i}`} href={s.best.url} target="_blank" rel="noreferrer" title="Ürün sayfasına git"
                                  className="inline-flex shrink-0 items-center gap-1 rounded-md bg-brand-green/10 px-2 py-1 text-[11px] font-semibold text-brand-green transition-colors hover:bg-brand-green/20">
                                  Ürüne git <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : (
                                <span className="shrink-0 text-[10px] text-slate-300">link yok</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {otherSources.length > 0 && (
                        <div data-testid="market-researched-sites">
                          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Taranan diğer siteler ({otherSources.length}) — sonuç yok</p>
                          <div className="flex flex-wrap gap-1">
                            {otherSources.map((s, i) => (
                              <span key={i} data-testid={`market-other-site-${i}`} title={s.site} className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[10px] text-slate-400 ring-1 ring-inset ring-slate-100">
                                {s.site}<span className="text-slate-300">·{s.status === "BLOCKED" ? "engel" : s.status === "ERROR" ? "hata" : "yok"}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Henüz araştırma yapılmadı. "Piyasa Araştır" ile perakende ve toptan sitelerini tarayın.</p>
                  )}
                  {ma.status === "BENCHMARK" && (
                    <p className="mt-2 text-[10px] text-slate-400" data-testid="benchmark-note">
                      İçe aktarma piyasa benchmarkı{ma.count ? ` · ${ma.count} gözlem` : ""}{ma.competitors ? ` · ${ma.competitors} rakip` : ""}{ma.confidence ? ` · güven: ${ma.confidence}` : ""}. Canlı doğrulama için "Piyasa Araştır"a basın.
                    </p>
                  )}
                  <p className="mt-2 text-[10px] text-slate-400">
                    {research.status === "SEEDED" && "Örnek veri · canlı tarama için 'Piyasa Araştır'a basın"}
                    {research.checked_at && research.status !== "SEEDED" && `Son tarama: ${new Date(research.checked_at).toLocaleString("tr-TR")} · ${research.sources_ok || 0}/${research.sources_total || 19} site yanıt verdi`}
                  </p>
                </TabsContent>
              )}

              {/* --- Alış & Fırsat --- */}
              {showPurchase && (
                <TabsContent value="purchase" className="mt-0">
                  {opp?.found && (
                    <div data-testid="opportunity-banner" className="mb-3 flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-emerald-600"><Sparkles className="h-5 w-5 text-white" /></div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-emerald-800">Tedarik Fırsatı! <span className="font-mono">%{opp.saving_pct}</span> daha ucuz</p>
                        <p className="text-xs text-emerald-700">
                          <b>{opp.site}</b> bu ürünü <b className="tabular">{fmtTL(opp.price)}</b> satıyor — bizim alış fiyatımız <span className="tabular">{fmtTL(opp.our_cost)}</span> (kalem başı {fmtTL(opp.saving)} tasarruf).
                        </p>
                        {opp.url && <a href={opp.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline">Siteye git <ExternalLink className="h-3 w-3" /></a>}
                      </div>
                    </div>
                  )}
                  <div className="mb-2 flex items-center gap-1.5">
                    <History className="h-4 w-4 text-slate-400" /><p className="overline">Son 3 Alış Fiyatı</p>
                    <button data-testid="open-price-history" onClick={() => { onClose(); nav(`/fiyat-gecmisi?product=${p.id}`); }}
                      className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-brand-green hover:text-brand-green">
                      <History className="h-3 w-3" /> Tüm Fiyat Geçmişi
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {hist.length === 0 && <p className="col-span-3 text-sm text-slate-400">Alış kaydı yok.</p>}
                    {hist.map((h, i) => (
                      <div key={i} className={`rounded-md p-2.5 ${i === 0 ? "bg-brand-green text-white" : "bg-slate-50 text-slate-700"}`}>
                        <p className={`text-[10px] ${i === 0 ? "text-emerald-100" : "text-slate-400"}`}>{new Date(h.date).toLocaleDateString("tr-TR")}</p>
                        <p className="tabular text-base font-bold">{fmtTL(h.unit_cost)}</p>
                        <p className={`text-[10px] ${i === 0 ? "text-emerald-100" : "text-slate-400"}`}>{h.supplier}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}

              {/* --- AI & Etiket --- */}
              {canEdit && (
                <TabsContent value="tools" className="mt-0 space-y-3">
                  <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                    <Printer className="h-4 w-4 text-slate-400" />
                    <span className="text-xs text-slate-500">Etiket adedi</span>
                    <input data-testid="label-copies" type="number" min="1" value={labelCopies}
                      onChange={(e) => setLabelCopies(e.target.value)} className="h-8 w-16 rounded-md border border-input px-2 text-sm tabular" />
                    <Button size="sm" variant="outline" data-testid="print-label-btn" onClick={printLabel} disabled={printingLabel} className="ml-auto h-8 text-xs">
                      {printingLabel ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Printer className="mr-1 h-3.5 w-3.5" />} Barkod/Fiyat Etiketi Yazdır
                    </Button>
                  </div>

                  <div className="rounded-md border border-brand-green/30 bg-emerald-50/50 p-3" data-testid="ai-desc-box">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <Bot className="h-4 w-4 text-brand-green" />
                      <span className="text-xs font-semibold text-slate-700">AI ile Satış Metni Üret</span>
                      <select data-testid="ai-desc-channel" value={descChannel} onChange={(e) => setDescChannel(e.target.value)}
                        className="ml-auto h-8 rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green">
                        <option value="marketplace">Pazaryeri</option>
                        <option value="web">Kendi Site</option>
                        <option value="wholesale">Toptan</option>
                      </select>
                      <Button size="sm" data-testid="ai-desc-btn" onClick={genDesc} disabled={aiBusy} className="h-8 bg-brand-green text-xs text-white hover:bg-brand-green-600">
                        {aiBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />} Üret
                      </Button>
                    </div>
                    {aiDesc && (
                      <div data-testid="ai-desc-result" className="space-y-2 rounded-md bg-white p-3 ring-1 ring-inset ring-slate-100">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-heading text-sm font-bold text-slate-800">{aiDesc.title}</p>
                          <button data-testid="ai-desc-copy" onClick={copyDesc} className="shrink-0 rounded-sm p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-green"><Copy className="h-4 w-4" /></button>
                        </div>
                        {aiDesc.description && <p className="whitespace-pre-wrap text-xs text-slate-600">{aiDesc.description}</p>}
                        {(aiDesc.bullets || []).length > 0 && (
                          <ul className="list-inside list-disc space-y-0.5 text-xs text-slate-600">
                            {aiDesc.bullets.map((b, i) => <li key={i}>{b}</li>)}
                          </ul>
                        )}
                        {(aiDesc.keywords || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-1">
                            {aiDesc.keywords.map((k, i) => <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">{k}</span>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>
              )}
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
